import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import AppImage from '../AppImage';
import AssessmentDropdown from '../AssessmentDropdown';
import { useAuth } from '../../contexts/AuthContext';
import progressTrackingService from '../../services/progressTrackingService';

const Header = ({ onAuthRequired, onLogout: propOnLogout }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [showAssessments, setShowAssessments] = useState(false);
  const [userProgress, setUserProgress] = useState({
    assessment_completed: false,
    questionnaire_completed: false,
    ai_plan_generated: false
  });
  const { user, userProfile, isPremium, signOut } = useAuth();
  
  // Load user progress when user changes
  useEffect(() => {
    const loadUserProgress = async () => {
      if (user?.id) {
        try {
          const progress = await progressTrackingService.getNavigationProgress(user.id);
          setUserProgress(progress);
        } catch (error) {
          console.error('Error loading user progress:', error);
        }
      } else {
        setUserProgress({
          assessment_completed: false,
          questionnaire_completed: false,
          ai_plan_generated: false
        });
      }
    };

    loadUserProgress();
  }, [user?.id]);

  // Debug logging (temporarily enabled)
  // console.log('Header Debug - user:', user);
  // console.log('Header Debug - userProfile:', userProfile);
  // console.log('Header Debug - isPremium:', isPremium);

  const handleLogout = async (e) => {
    try {
      console.log('Header handleLogout called');
      e?.preventDefault();
      e?.stopPropagation();
      
      console.log('Calling signOut from AuthContext...');
      // Call the signOut function from AuthContext
      await signOut();
      console.log('signOut completed successfully');
      
      // Close any open menus
      setIsMoreMenuOpen(false);
      setIsMobileMenuOpen(false);
      
      console.log('Redirecting after sign-out...');
      // Force redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      // Redirect to home even if signOut throws
      window.location.href = '/';
    }
  };

  const allNavigationItems = [
    { name: 'Dashboard', href: '/progress-dashboard', icon: 'Home', isPremium: false },
    { 
      name: 'Assessment', 
      href: '/career-assessment-setup', 
      icon: 'Target', 
      isPremium: false,
      isCompleted: userProgress.assessment_completed
    },
    { 
      name: 'Questionnaire', 
      href: '/adaptive-questionnaire', 
      icon: 'BookOpen', 
      isPremium: false,
      isCompleted: userProgress.questionnaire_completed
    },
    { 
      name: 'AI Plan', 
      href: '/ai-plan-generation', 
      icon: 'CheckCircle', 
      isPremium: false, 
      isLimited: true, 
      limitLabel: '1/mo',
      isCompleted: userProgress.ai_plan_generated
    },
    { name: 'Exercises', href: '/exercise-management', icon: 'BarChart3', isPremium: false, isLimited: true, limitLabel: '2/week' },
    { name: 'Career Guidance', href: '/career-advancement-guidance', icon: 'TrendingUp', isPremium: true },
    { name: 'Salary Insights', href: '/salary-insights', icon: 'DollarSign', isPremium: true },
    { name: 'Resume Content Kit', href: '/resume', icon: 'FileText', isPremium: true },
    { name: 'Job Tracker', href: '/jobs', icon: 'Briefcase', isPremium: true },
    { name: 'Credits', href: '/credits', icon: 'Coins', isPremium: false, isSpecial: true },
    { name: 'Subscription', href: '/subscription-management', icon: 'CreditCard', isPremium: false, isSpecial: true },
  ];

  // Show all navigation items for all users, but handle premium features differently
  const navigationItems = allNavigationItems;

  // Progress tracking for navigation items

  const isActivePath = (path) => {
    if (path === '/progress-dashboard') {
      return location?.pathname === '/' || location?.pathname === path;
    }
    return location?.pathname?.startsWith(path);
  };

  const handleNavigation = (item, e) => {
    // Check if this is a premium feature and user is not premium
    if (item?.isPremium && !isPremium) {
      e?.preventDefault();
      // Show upgrade prompt instead of navigating
      const upgradeMessage = `"${item.name}" is a premium feature. Upgrade to Premium to access AI-powered career insights, advanced analytics, and more!`;
      alert(upgradeMessage + '\n\nRedirecting to subscription management...');
      // Redirect to subscription management
      window.location.href = '/subscription-management';
      return;
    }

    if (item?.authRequired && !user) {
      onAuthRequired?.();
      return;
    }
    setIsMobileMenuOpen(false);
    setIsMoreMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo Section - Centered and taller */}
        <div className="flex items-center justify-center py-1 border-b border-border/50">
          <Link to="/" className="flex items-center text-primary hover:text-primary/90 transition-colors duration-200">
            <AppImage 
              src="/assets/images/SkillBinder_Logo_PNG_format-1757444619599.png"
              alt="SkillBinder Logo"
              className="h-10 w-auto"
            />
            <div className="w-[50px]" />
            <AppImage 
              src="/assets/images/CareerLift_Logo-1757425914868.png"
              alt="CareerLift Logo"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        {/* Upgrade Banner for Free Users */}
        {user && !isPremium && (
          <div className="px-4 sm:px-6 lg:px-8 mb-3">
            <div className="max-w-7xl mx-auto">
              <div className="bg-gradient-to-r from-warning/10 to-primary/10 border border-warning/20 rounded-lg p-3">
                <div className="flex items-center justify-center space-x-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="Crown" size={20} className="text-warning" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">
                        Unlock Premium Features
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Get Career Guidance, Salary Insights, Resume Builder & unlimited exercises
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/subscription-management"
                    className="bg-warning hover:bg-warning/90 text-warning-foreground px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                  >
                    <Icon name="Sparkles" size={14} />
                    <span>Upgrade</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Section */}
        <div className="relative flex flex-col items-center justify-center py-3">
          {/* Desktop Navigation - Two Lines */}
          <div className="hidden md:flex flex-col items-center justify-center space-y-2">
            {/* First Row - Core Navigation */}
            <nav className="flex items-center justify-center space-x-1">
              {navigationItems?.slice(0, 6).map((item) => (
                <Link
                  key={item?.href}
                  to={item?.href}
                  onClick={(e) => handleNavigation(item, e)}
                  className={(() => {
                    const base = 'relative px-3 py-2 rounded-full text-xs font-medium transition-all duration-200 ease-in-out flex items-center space-x-1.5 group';
                    const isActive = isActivePath(item?.href);
                    const isDashboard = item?.href === '/progress-dashboard';
                    const isSpecial = item?.isSpecial;
                    const isCompleted = item?.isCompleted;
                    
                    if (isDashboard) {
                      // SkillBinder blue highlight for Dashboard at all times
                      return `${base} ${isActive ? 'bg-[#17394B] text-white shadow-sm' : 'text-[#17394B] border border-[#17394B]/50 bg-[#17394B]/5 hover:bg-[#17394B]/10'}`;
                    }
                    if (isSpecial) {
                      // Special colored pill for Credits and Subscription
                      return `${base} ${isActive ? 'bg-[#E3B549] text-[#17394B] font-semibold shadow-sm' : 'bg-[#E3B549]/20 text-[#17394B] border border-[#E3B549]/30 hover:bg-[#E3B549]/30'}`;
                    }
                    if (isCompleted) {
                      // Green pill for completed items
                      return `${base} ${isActive ? 'bg-green-600 text-white shadow-sm' : 'bg-green-500/20 text-green-700 border border-green-500/30 hover:bg-green-500/30'}`;
                    }
                    return `${base} ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'}`;
                  })()}
                  title={item?.tooltip}
                >
                  <Icon 
                    name={item?.icon} 
                    size={14} 
                    className={(() => {
                      const isActive = isActivePath(item?.href);
                      const isDashboard = item?.href === '/progress-dashboard';
                      const isSpecial = item?.isSpecial;
                      
                      if (isDashboard) {
                        return isActive ? 'text-white' : 'text-[#17394B]';
                      }
                      if (isSpecial) {
                        return isActive ? 'text-[#17394B]' : 'text-[#17394B]';
                      }
                      return isActive ? 'text-primary-foreground' : 'text-current';
                    })()} 
                  />
                  <span>{item?.name}</span>
                  {item?.isPremium && (
                    <Icon 
                      name="Crown" 
                      size={10} 
                      className={(() => {
                        const isActive = isActivePath(item?.href);
                        const isDashboard = item?.href === '/progress-dashboard';
                        if (isDashboard) {
                          return isActive ? 'text-white' : 'text-[#E3B549]';
                        }
                        return `${isActive ? 'text-primary-foreground' : 'text-warning'} transition-colors duration-200`;
                      })()}
                      title="Premium Feature"
                    />
                  )}
                  {item?.isLimited && !isPremium && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-1 py-0.5 rounded-full">
                      {item?.limitLabel || '2/week'}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
            
            {/* Second Row - Additional Navigation */}
            <nav className="flex items-center justify-center space-x-1">
              {navigationItems?.slice(6).map((item) => (
                <Link
                  key={item?.href}
                  to={item?.href}
                  onClick={(e) => handleNavigation(item, e)}
                  className={(() => {
                    const base = 'relative px-3 py-2 rounded-full text-xs font-medium transition-all duration-200 ease-in-out flex items-center space-x-1.5 group';
                    const isActive = isActivePath(item?.href);
                    const isDashboard = item?.href === '/progress-dashboard';
                    const isSpecial = item?.isSpecial;
                    const isCompleted = item?.isCompleted;
                    
                    if (isDashboard) {
                      // SkillBinder blue highlight for Dashboard at all times
                      return `${base} ${isActive ? 'bg-[#17394B] text-white shadow-sm' : 'text-[#17394B] border border-[#17394B]/50 bg-[#17394B]/5 hover:bg-[#17394B]/10'}`;
                    }
                    if (isSpecial) {
                      // Special colored pill for Credits and Subscription
                      return `${base} ${isActive ? 'bg-[#E3B549] text-[#17394B] font-semibold shadow-sm' : 'bg-[#E3B549]/20 text-[#17394B] border border-[#E3B549]/30 hover:bg-[#E3B549]/30'}`;
                    }
                    if (isCompleted) {
                      // Green pill for completed items
                      return `${base} ${isActive ? 'bg-green-600 text-white shadow-sm' : 'bg-green-500/20 text-green-700 border border-green-500/30 hover:bg-green-500/30'}`;
                    }
                    return `${base} ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'}`;
                  })()}
                  title={item?.tooltip}
                >
                  <Icon 
                    name={item?.icon} 
                    size={14} 
                    className={(() => {
                      const isActive = isActivePath(item?.href);
                      const isDashboard = item?.href === '/progress-dashboard';
                      const isSpecial = item?.isSpecial;
                      
                      if (isDashboard) {
                        return isActive ? 'text-white' : 'text-[#17394B]';
                      }
                      if (isSpecial) {
                        return isActive ? 'text-[#17394B]' : 'text-[#17394B]';
                      }
                      return isActive ? 'text-primary-foreground' : 'text-current';
                    })()} 
                  />
                  <span>{item?.name}</span>
                  {item?.isPremium && (
                    <Icon 
                      name="Crown" 
                      size={10} 
                      className={(() => {
                        const isActive = isActivePath(item?.href);
                        const isDashboard = item?.href === '/progress-dashboard';
                        if (isDashboard) {
                          return isActive ? 'text-white' : 'text-[#E3B549]';
                        }
                        return `${isActive ? 'text-primary-foreground' : 'text-warning'} transition-colors duration-200`;
                      })()}
                      title="Premium Feature"
                    />
                  )}
                  {item?.isLimited && !isPremium && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-1 py-0.5 rounded-full">
                      {item?.limitLabel || '2/week'}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>

          {/* User Menu / Auth Button - Absolute positioned on right */}
          <div className="absolute right-0 hidden md:flex items-center">
            {user ? (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                  className="flex items-center space-x-2 text-muted-foreground hover:text-foreground"
                >
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={14} color="white" />
                  </div>
                <span className="text-sm font-medium text-foreground">
                  {userProfile?.first_name && userProfile?.last_name 
                    ? `${userProfile.first_name} ${userProfile.last_name}`
                    : userProfile?.first_name || userProfile?.full_name || (user?.email === 'info@skillbinder.com' ? 'Glenn Donnelly' : 'User')
                  }
                </span>
                  {isPremium && (
                    <Icon name="Crown" size={14} className="text-warning" />
                  )}
                  <Icon name="ChevronDown" size={14} />
                </Button>

                {isMoreMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-border">
                      <p className="text-sm font-medium text-popover-foreground">
                        {userProfile?.first_name && userProfile?.last_name 
                          ? `${userProfile.first_name} ${userProfile.last_name}`
                          : userProfile?.first_name || userProfile?.full_name || user?.email?.split('@')[0] || 'User'
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                    
                    {/* Assessments Section */}
                    <AssessmentDropdown 
                      userId={user?.id} 
                      onClose={() => setIsMoreMenuOpen(false)}
                    />
                    
                    <div className="border-t border-border">
                      <Link
                        to="/account-settings"
                        onClick={() => setIsMoreMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 w-full text-left"
                      >
                        <Icon name="Settings" size={16} />
                        <span>Account Settings</span>
                      </Link>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLogout(e);
                          setIsMoreMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 w-full text-left"
                      >
                        <Icon name="LogOut" size={16} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => onAuthRequired?.()}
                iconName="LogIn"
                iconPosition="left"
              >
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.href}
                  to={item?.href}
                  onClick={(e) => handleNavigation(item, e)}
                  className={(() => {
                    const base = 'flex items-center space-x-3 px-3 py-2 rounded-full text-base font-medium transition-colors duration-200';
                    const isActive = isActivePath(item?.href);
                    const isDashboard = item?.href === '/progress-dashboard';
                    const isSpecial = item?.isSpecial;
                    const isCompleted = item?.isCompleted;
                    
                    if (isDashboard) {
                      return `${base} ${isActive ? 'bg-[#17394B] text-white' : 'text-[#17394B] border border-[#17394B]/50 bg-[#17394B]/5'}`;
                    }
                    if (isSpecial) {
                      return `${base} ${isActive ? 'bg-[#E3B549] text-[#17394B] font-semibold' : 'bg-[#E3B549]/20 text-[#17394B] border border-[#E3B549]/30'}`;
                    }
                    if (isCompleted) {
                      return `${base} ${isActive ? 'bg-green-600 text-white' : 'bg-green-500/20 text-green-700 border border-green-500/30'}`;
                    }
                    return `${base} ${isActive ? 'bg-primary text-primary-foreground' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'}`;
                  })()}
                >
                  <Icon 
                    name={item?.icon} 
                    size={18} 
                    className={(() => {
                      const isActive = isActivePath(item?.href);
                      const isDashboard = item?.href === '/progress-dashboard';
                      const isSpecial = item?.isSpecial;
                      
                      if (isDashboard) {
                        return isActive ? 'text-white' : 'text-[#17394B]';
                      }
                      if (isSpecial) {
                        return isActive ? 'text-[#17394B]' : 'text-[#17394B]';
                      }
                      return '';
                    })()} 
                  />
                  <span className="flex-1">{item?.name}</span>
                  {item?.isPremium && (
                    <Icon 
                      name="Crown" 
                      size={14} 
                      className={(() => {
                        const isActive = isActivePath(item?.href);
                        const isDashboard = item?.href === '/progress-dashboard';
                        if (isDashboard) {
                          return isActive ? 'text-white' : 'text-[#E3B549]';
                        }
                        return `${isActive ? 'text-primary-foreground' : 'text-warning'} transition-colors duration-200`;
                      })()}
                      title="Premium Feature"
                    />
                  )}
                  {item?.isLimited && !isPremium && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded-full">
                      {item?.limitLabel || '2/week'}
                    </span>
                  )}
                </Link>
              ))}

              <hr className="my-2 border-border" />

              {user ? (
                <div className="px-3 py-2">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {userProfile?.first_name && userProfile?.last_name 
                          ? `${userProfile.first_name} ${userProfile.last_name}`
                          : userProfile?.first_name || userProfile?.full_name || 'User'
                        }
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {user?.email}
                      </div>
                    </div>
                    {isPremium && (
                      <Icon name="Crown" size={16} className="text-warning ml-auto" />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout(e);
                      setIsMobileMenuOpen(false);
                    }}
                    iconName="LogOut"
                    iconPosition="left"
                    fullWidth
                  >
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="px-3 py-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      onAuthRequired?.();
                      setIsMobileMenuOpen(false);
                    }}
                    iconName="LogIn"
                    iconPosition="left"
                    fullWidth
                  >
                    Sign In
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close more menu */}
      {isMoreMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsMoreMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;